import { useEffect, useState, useRef } from 'react';
import { useAuth } from '../lib/auth';
import api, { BACKEND_URL } from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Handshake, User, MapPin, CheckCircle, Edit, X, Save, Upload,
  Trash2, Download, File, FileText, Image, CalendarDays, Plus, Camera
} from 'lucide-react';
import { toast } from 'sonner';

export default function PartnerDashboard() {
  const { user } = useAuth();
  const [partner, setPartner] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [slots, setSlots] = useState([]);
  const [editingProfile, setEditingProfile] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [form, setForm] = useState({});
  const [slotForm, setSlotForm] = useState({ title: '', description: '', start_date: '', end_date: '', capacity: 1, price_range: '' });
  const [addingSlot, setAddingSlot] = useState(false);
  const fileInputRef = useRef(null);
  const photoInputRef = useRef(null);

  const STATUS_LABELS = { pending: 'En attente de validation', active: 'Actif', rejected: 'Refusé' };
  const STATUS_COLORS = { pending: 'bg-orange-100 text-orange-700', active: 'bg-green-100 text-green-700', rejected: 'bg-red-100 text-red-700' };

  const loadData = () => {
    api.get('/partners/mine').then(r => {
      setPartner(r.data);
      api.get(`/partners/${r.data.id}/photos`).then(pr => setPhotos(pr.data)).catch(() => {});
      api.get(`/partners/${r.data.id}/slots`).then(sr => setSlots(sr.data)).catch(() => {});
    }).catch(() => {});
    api.get('/documents/mine').then(r => setDocuments(r.data.filter(d => !d.content_type?.startsWith('image/')))).catch(() => {});
  };

  useEffect(() => { loadData(); }, []);

  const openEdit = () => {
    setForm({
      contact_phone: partner?.contact_phone || '',
      covered_zone: partner?.covered_zone || '',
      description: partner?.description || '',
      accessibility_level: partner?.accessibility_level || '',
      availability: partner?.availability || '',
    });
    setEditingProfile(true);
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.put('/partners/mine', form);
      setPartner(res.data);
      toast.success('Profil partenaire mis à jour');
      setEditingProfile(false);
    } catch { toast.error('Erreur lors de la mise à jour'); }
    finally { setSaving(false); }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !partner) return;
    if (!file.type.startsWith('image/')) { toast.error('Sélectionnez une image (jpg, png, webp...)'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Image trop grande (max 10 Mo)'); return; }
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/partners/${partner.id}/photos`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Photo ajoutée avec succès');
      const pr = await api.get(`/partners/${partner.id}/photos`);
      setPhotos(pr.data);
    } catch { toast.error('Erreur lors du téléversement'); }
    finally { setUploadingPhoto(false); e.target.value = ''; }
  };

  const deletePhoto = async (docId) => {
    if (!window.confirm('Supprimer cette photo ?')) return;
    try {
      await api.delete(`/partners/${partner.id}/photos/${docId}`);
      setPhotos(p => p.filter(x => x.id !== docId));
      toast.success('Photo supprimée');
    } catch { toast.error('Erreur'); }
  };

  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Fichier trop grand (max 10 Mo)'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${file.name} téléversé avec succès`);
      loadData();
    } catch { toast.error('Erreur lors du téléversement'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const deleteDocument = async (docId, name) => {
    if (!window.confirm(`Supprimer "${name}" ?`)) return;
    try {
      await api.delete(`/documents/${docId}`);
      toast.success('Document supprimé');
      setDocuments(d => d.filter(x => x.id !== docId));
    } catch { toast.error('Erreur'); }
  };

  const downloadDoc = (docId) => window.open(`${BACKEND_URL}/api/documents/${docId}/download`, '_blank');

  const createSlot = async (e) => {
    e.preventDefault();
    if (!slotForm.title || !slotForm.start_date || !slotForm.end_date) {
      toast.error('Titre et dates sont obligatoires'); return;
    }
    try {
      await api.post(`/partners/${partner.id}/slots`, slotForm);
      toast.success('Créneau ajouté');
      setSlotForm({ title: '', description: '', start_date: '', end_date: '', capacity: 1, price_range: '' });
      setAddingSlot(false);
      const sr = await api.get(`/partners/${partner.id}/slots`);
      setSlots(sr.data);
    } catch { toast.error('Erreur lors de la création'); }
  };

  const deleteSlot = async (slotId) => {
    if (!window.confirm('Supprimer ce créneau ?')) return;
    try {
      await api.delete(`/slots/${slotId}`);
      setSlots(s => s.filter(x => x.id !== slotId));
      toast.success('Créneau supprimé');
    } catch { toast.error('Erreur'); }
  };

  const formatSize = (bytes) => bytes < 1024 * 1024 ? `${(bytes / 1024).toFixed(0)} Ko` : `${(bytes / 1024 / 1024).toFixed(1)} Mo`;
  const slotStatusColors = { available: 'bg-green-100 text-green-700', full: 'bg-red-100 text-red-700', closed: 'bg-gray-100 text-gray-600' };
  const slotStatusLabels = { available: 'Disponible', full: 'Complet', closed: 'Fermé' };

  return (
    <div className="min-h-screen bg-background py-24 md:py-28" data-testid="partner-dashboard">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground mb-2">Espace Partenaire</h1>
        <p className="text-muted-foreground mb-6">Bienvenue {user?.first_name}</p>

        {partner ? (
          <>
            {/* Header card */}
            <div className="bg-white rounded-2xl p-5 border border-border/50 shadow-sm mb-6 flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-secondary/10 flex items-center justify-center flex-shrink-0">
                <Handshake className="w-7 h-7 text-secondary" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-heading text-lg font-semibold truncate">{partner.company_name}</h2>
                <div className="flex items-center gap-2 flex-wrap mt-1">
                  <Badge className={`text-xs ${STATUS_COLORS[partner.status]}`}>{STATUS_LABELS[partner.status]}</Badge>
                  <span className="text-xs text-muted-foreground">{partner.category}</span>
                  {partner.covered_zone && <span className="text-xs text-muted-foreground">· {partner.covered_zone}</span>}
                </div>
              </div>
            </div>

            <Tabs defaultValue="profil" className="space-y-6">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="profil" data-testid="tab-profil">Profil</TabsTrigger>
                <TabsTrigger value="photos" data-testid="tab-photos">
                  <Camera className="w-3.5 h-3.5 mr-1.5" />Photos
                </TabsTrigger>
                <TabsTrigger value="creneaux" data-testid="tab-creneaux">
                  <CalendarDays className="w-3.5 h-3.5 mr-1.5" />Créneaux
                </TabsTrigger>
                <TabsTrigger value="documents" data-testid="tab-documents">Docs</TabsTrigger>
              </TabsList>

              {/* ===== PROFIL ===== */}
              <TabsContent value="profil">
                <div className="bg-white rounded-2xl p-6 border border-border/50 shadow-sm">
                  {!editingProfile ? (
                    <>
                      <div className="flex justify-end mb-4">
                        <Button variant="ghost" size="sm" onClick={openEdit} className="rounded-xl" data-testid="partner-edit-profile-btn">
                          <Edit className="w-4 h-4 mr-2" /> Modifier
                        </Button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm" data-testid="partner-profile-info">
                        <div className="flex items-start gap-3">
                          <User className="w-4 h-4 text-muted-foreground mt-0.5" />
                          <div>
                            <p className="font-medium">Contact</p>
                            <p className="text-muted-foreground">{partner.contact_first_name} {partner.contact_last_name}</p>
                            <p className="text-muted-foreground">{partner.contact_email}</p>
                            {partner.contact_phone && <p className="text-muted-foreground">{partner.contact_phone}</p>}
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
                          <div>
                            <p className="font-medium">Zone couverte</p>
                            <p className="text-muted-foreground">{partner.covered_zone || '—'}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <CheckCircle className="w-4 h-4 text-muted-foreground mt-0.5" />
                          <div>
                            <p className="font-medium">Accessibilité</p>
                            <p className="text-muted-foreground">{partner.accessibility_level || '—'}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <CheckCircle className="w-4 h-4 text-muted-foreground mt-0.5" />
                          <div>
                            <p className="font-medium">Disponibilité générale</p>
                            <p className="text-muted-foreground">{partner.availability || '—'}</p>
                          </div>
                        </div>
                        {partner.description && (
                          <div className="md:col-span-2 pt-4 border-t border-border/30">
                            <p className="text-sm font-medium mb-1">Description</p>
                            <p className="text-sm text-muted-foreground">{partner.description}</p>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <form onSubmit={saveProfile} className="space-y-4" data-testid="partner-edit-form">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-medium text-sm">Modifier les informations</h3>
                        <button type="button" onClick={() => setEditingProfile(false)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div><Label>Téléphone</Label>
                          <Input value={form.contact_phone} onChange={e => setForm(p => ({ ...p, contact_phone: e.target.value }))} className="mt-1 rounded-xl" data-testid="partner-phone-input" /></div>
                        <div><Label>Zone couverte</Label>
                          <Input value={form.covered_zone} onChange={e => setForm(p => ({ ...p, covered_zone: e.target.value }))} className="mt-1 rounded-xl" data-testid="partner-zone-input" /></div>
                        <div><Label>Accessibilité</Label>
                          <Input value={form.accessibility_level} onChange={e => setForm(p => ({ ...p, accessibility_level: e.target.value }))} className="mt-1 rounded-xl" data-testid="partner-accessibility-input" /></div>
                        <div><Label>Disponibilité</Label>
                          <Input value={form.availability} onChange={e => setForm(p => ({ ...p, availability: e.target.value }))} className="mt-1 rounded-xl" data-testid="partner-availability-input" /></div>
                        <div className="sm:col-span-2"><Label>Description</Label>
                          <Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} className="mt-1 rounded-xl min-h-[80px]" data-testid="partner-description-input" /></div>
                      </div>
                      <div className="flex gap-3 pt-2">
                        <Button type="submit" disabled={saving} className="rounded-full" data-testid="partner-save-btn">
                          <Save className="w-4 h-4 mr-2" />{saving ? 'Enregistrement...' : 'Enregistrer'}
                        </Button>
                        <Button type="button" variant="outline" onClick={() => setEditingProfile(false)} className="rounded-full">Annuler</Button>
                      </div>
                    </form>
                  )}
                </div>
              </TabsContent>

              {/* ===== PHOTOS ===== */}
              <TabsContent value="photos">
                <div className="bg-white rounded-2xl p-6 border border-border/50 shadow-sm" data-testid="partner-photos-section">
                  <div className="flex items-center justify-between mb-5">
                    <div>
                      <h2 className="font-heading text-lg font-semibold">Galerie de photos</h2>
                      <p className="text-xs text-muted-foreground mt-1">Présentez vos hébergements, équipements et activités adaptées</p>
                    </div>
                    <div>
                      <input ref={photoInputRef} type="file" className="hidden" accept="image/*" onChange={handlePhotoUpload} data-testid="photo-file-input" />
                      <Button size="sm" onClick={() => photoInputRef.current?.click()} disabled={uploadingPhoto} className="rounded-full" data-testid="add-photo-btn">
                        <Camera className="w-4 h-4 mr-2" />{uploadingPhoto ? 'Envoi...' : 'Ajouter une photo'}
                      </Button>
                    </div>
                  </div>

                  {photos.length === 0 ? (
                    <div className="text-center py-12 border-2 border-dashed border-border/40 rounded-xl cursor-pointer hover:border-primary/30 transition-colors" onClick={() => photoInputRef.current?.click()} data-testid="empty-photo-gallery">
                      <Image className="w-12 h-12 mx-auto mb-3 text-muted-foreground/20" />
                      <p className="text-sm font-medium text-muted-foreground">Aucune photo pour l'instant</p>
                      <p className="text-xs text-muted-foreground mt-1">Cliquez pour ajouter des photos de vos installations</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="photo-gallery">
                      {photos.map(photo => (
                        <div key={photo.id} className="relative group aspect-square rounded-xl overflow-hidden bg-muted/30 border border-border/30" data-testid={`photo-item-${photo.id}`}>
                          <img
                            src={`${BACKEND_URL}/api/photos/${photo.id}`}
                            alt={photo.original_filename}
                            className="w-full h-full object-cover transition-transform group-hover:scale-105"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                            <button onClick={() => deletePhoto(photo.id)} className="w-9 h-9 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 transition-colors" data-testid={`delete-photo-${photo.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <p className="text-white text-xs truncate">{photo.original_filename}</p>
                          </div>
                        </div>
                      ))}
                      {/* Upload tile */}
                      <button onClick={() => photoInputRef.current?.click()} className="aspect-square rounded-xl border-2 border-dashed border-border/40 hover:border-primary/40 transition-colors flex flex-col items-center justify-center gap-2 text-muted-foreground hover:text-primary" data-testid="add-photo-tile">
                        <Plus className="w-6 h-6" />
                        <span className="text-xs">Ajouter</span>
                      </button>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* ===== CRÉNEAUX ===== */}
              <TabsContent value="creneaux">
                <div className="bg-white rounded-2xl p-6 border border-border/50 shadow-sm" data-testid="partner-slots-section">
                  <div className="flex items-center justify-between mb-5">
                    <div>
                      <h2 className="font-heading text-lg font-semibold">Créneaux de disponibilité</h2>
                      <p className="text-xs text-muted-foreground mt-1">Définissez vos disponibilités pour les séjours de répit</p>
                    </div>
                    <Button size="sm" onClick={() => setAddingSlot(true)} className="rounded-full" data-testid="add-slot-btn" disabled={addingSlot}>
                      <Plus className="w-4 h-4 mr-2" />Ajouter un créneau
                    </Button>
                  </div>

                  {addingSlot && (
                    <form onSubmit={createSlot} className="mb-6 p-4 rounded-xl bg-muted/30 border border-border/40 space-y-3" data-testid="slot-form">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-medium text-sm">Nouveau créneau</h3>
                        <button type="button" onClick={() => setAddingSlot(false)} className="text-muted-foreground"><X className="w-4 h-4" /></button>
                      </div>
                      <div>
                        <Label>Titre *</Label>
                        <Input value={slotForm.title} onChange={e => setSlotForm(p => ({ ...p, title: e.target.value }))} placeholder="Ex: Semaine découverte Provence" className="mt-1 rounded-xl" required data-testid="slot-title-input" />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>Date de début *</Label>
                          <input type="date" value={slotForm.start_date} onChange={e => setSlotForm(p => ({ ...p, start_date: e.target.value }))} className="mt-1 w-full h-10 px-3 rounded-xl border border-input bg-white text-sm" required data-testid="slot-start-date" />
                        </div>
                        <div>
                          <Label>Date de fin *</Label>
                          <input type="date" value={slotForm.end_date} onChange={e => setSlotForm(p => ({ ...p, end_date: e.target.value }))} className="mt-1 w-full h-10 px-3 rounded-xl border border-input bg-white text-sm" required data-testid="slot-end-date" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>Capacité (places)</Label>
                          <Input type="number" min="1" value={slotForm.capacity} onChange={e => setSlotForm(p => ({ ...p, capacity: parseInt(e.target.value) || 1 }))} className="mt-1 rounded-xl" data-testid="slot-capacity" />
                        </div>
                        <div>
                          <Label>Fourchette de prix</Label>
                          <Input value={slotForm.price_range} onChange={e => setSlotForm(p => ({ ...p, price_range: e.target.value }))} placeholder="Ex: 1500-2500€" className="mt-1 rounded-xl" data-testid="slot-price" />
                        </div>
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Textarea value={slotForm.description} onChange={e => setSlotForm(p => ({ ...p, description: e.target.value }))} placeholder="Prestations incluses, équipements disponibles…" className="mt-1 rounded-xl min-h-[60px]" data-testid="slot-description" />
                      </div>
                      <div className="flex gap-3 pt-1">
                        <Button type="submit" className="rounded-full" data-testid="slot-submit-btn">Créer le créneau</Button>
                        <Button type="button" variant="outline" onClick={() => setAddingSlot(false)} className="rounded-full">Annuler</Button>
                      </div>
                    </form>
                  )}

                  {slots.length === 0 ? (
                    <div className="text-center py-10 text-muted-foreground">
                      <CalendarDays className="w-10 h-10 mx-auto mb-3 opacity-20" />
                      <p className="text-sm">Aucun créneau défini. Créez vos premières disponibilités.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {slots.map(slot => (
                        <div key={slot.id} className="flex items-start gap-4 p-4 rounded-xl border border-border/40 hover:border-border/70 transition-colors" data-testid={`slot-item-${slot.id}`}>
                          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <CalendarDays className="w-5 h-5 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <h3 className="font-medium text-sm">{slot.title}</h3>
                              <Badge className={`text-xs ${slotStatusColors[slot.status]}`}>{slotStatusLabels[slot.status]}</Badge>
                              {slot.price_range && <span className="text-xs text-muted-foreground">{slot.price_range}</span>}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {new Date(slot.start_date).toLocaleDateString('fr-FR')} → {new Date(slot.end_date).toLocaleDateString('fr-FR')}
                              &nbsp;·&nbsp;{slot.booked_count}/{slot.capacity} réservation(s)
                            </p>
                            {slot.description && <p className="text-xs text-muted-foreground mt-1 italic">{slot.description}</p>}
                          </div>
                          <button onClick={() => deleteSlot(slot.id)} className="text-muted-foreground hover:text-destructive transition-colors mt-0.5 flex-shrink-0" data-testid={`delete-slot-${slot.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* ===== DOCUMENTS ===== */}
              <TabsContent value="documents">
                <div className="bg-white rounded-2xl p-6 border border-border/50 shadow-sm" data-testid="partner-documents-section">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="font-heading text-lg font-semibold">Documents partenaire</h2>
                      <p className="text-xs text-muted-foreground mt-1">Certifications, agréments PMR, assurances…</p>
                    </div>
                    <div>
                      <input ref={fileInputRef} type="file" className="hidden" onChange={handleDocUpload} accept=".pdf,.doc,.docx" data-testid="partner-file-input" />
                      <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="rounded-full" data-testid="partner-upload-btn">
                        <Upload className="w-4 h-4 mr-2" />{uploading ? 'Envoi...' : 'Ajouter un document'}
                      </Button>
                    </div>
                  </div>
                  {documents.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <File className="w-10 h-10 mx-auto mb-3 opacity-20" />
                      <p className="text-sm">Aucun document. Ajoutez vos justificatifs.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {documents.map(doc => (
                        <div key={doc.id} className="flex items-center gap-3 p-3 rounded-xl bg-muted/30 border border-border/30" data-testid={`partner-doc-item-${doc.id}`}>
                          <FileText className="w-5 h-5 text-secondary flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{doc.original_filename}</p>
                            <p className="text-xs text-muted-foreground">{formatSize(doc.size)} · {new Date(doc.created_at).toLocaleDateString('fr-FR')}</p>
                          </div>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => downloadDoc(doc.id)} className="h-8 w-8 p-0" data-testid={`partner-doc-download-${doc.id}`}>
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => deleteDocument(doc.id, doc.original_filename)} className="h-8 w-8 p-0" data-testid={`partner-doc-delete-${doc.id}`}>
                              <Trash2 className="w-4 h-4 text-destructive/70" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </>
        ) : (
          <div className="bg-white rounded-2xl p-10 text-center border border-border/50 shadow-sm">
            <Handshake className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="font-heading text-lg font-medium mb-2">Profil partenaire non trouvé</h3>
            <p className="text-muted-foreground text-sm">Votre profil est en cours de traitement. Contactez-nous si nécessaire.</p>
          </div>
        )}
      </div>
    </div>
  );
}
